import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import './PublicCampPage.css';

const API = window.location.origin + '/api';

/**
 * The public description page for one camp.
 *
 * Every section renders from camp.page_content and is omitted when its data is
 * absent, so a camp can publish a hero and a location and nothing else. A camp
 * with no page content at all redirects to the registration form — which is
 * where /camp/<slug> went before this page existed, so no shared link changes
 * meaning.
 */
export default function PublicCampPage() {
  const { slug } = useParams();
  const [camp, setCamp] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/camps/public/${slug}`)
      .then((r) => {
        setCamp(r.data);
        document.title = `${r.data.name} | ProZpects`;
        if (r.data.canonical_slug && r.data.canonical_slug !== slug) {
          window.history.replaceState(null, '', `/camp/${r.data.canonical_slug}`);
        }
      })
      .catch(() => setLoadError('This camp is not available.'));
  }, [slug]);

  if (loadError) {
    return <div className="pcp-empty"><p>{loadError}</p></div>;
  }
  if (!camp) return <div className="pcp-empty"><p>Loading…</p></div>;

  const page = camp.page_content;
  if (!page || !page.enabled) {
    return <Navigate to={`/camp/${camp.canonical_slug || slug}/register`} replace />;
  }

  const registerHref = `/camp/${camp.canonical_slug || slug}/register`;
  const dateStr = camp.date
    ? new Date(camp.date).toLocaleDateString('en-US',
      { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const Cta = ({ className, children }) => (
    <Link to={registerHref} className={className} data-testid="camp_page_register_cta">
      {children}
    </Link>
  );

  const has = (v) => Array.isArray(v) ? v.length > 0 : !!v;

  // page.location.maps_url is admin/partner-authored content rendered
  // straight into an <a href> on an unauthenticated page. React sets the
  // attribute even for a `javascript:` URL (it only warns), so a
  // lower-trust partner editing their own camp could stash an XSS payload
  // that fires for any visitor who clicks "Get Directions" -- including an
  // admin with a bearer token in localStorage. Only ever link an absolute
  // http(s) URL; anything else renders as plain text, never a dead or
  // dangerous anchor.
  const isSafeHttpUrl = (u) =>
    typeof u === 'string' && (u.startsWith('https://') || u.startsWith('http://'));

  // Guard on RENDERABLE CONTENT, not object presence: the page_content editor's
  // empty state initializes these as `{ name: '', address_lines: [], maps_url: '' }`
  // / `{ name: '', email: '', phone: '' }` rather than omitting the key entirely,
  // so `!!page.location` alone is true for a location with nothing to show —
  // which would render an orphan "Location" / "Questions" label above an empty box.
  const hasLocation = !!(page.location && (
    page.location.name || (page.location.address_lines || []).length || page.location.maps_url
  ));
  const hasContact = !!(page.contact && (
    page.contact.name || page.contact.email || page.contact.phone
  ));

  return (
    <div className="pcp">
      <nav className="pcp-nav">
        <div className="pcp-nav__left">
          <img src="/logo-wordmark.png" alt="ProZpects" className="pcp-nav__logo" />
        </div>
        <div className="pcp-nav__right">
          {has(page.schedule?.groups) && <a href="#schedule">Schedule</a>}
          {has(page.testing?.measurables) && <a href="#testing">Testing</a>}
          <Cta className="pcp-btn pcp-btn--green pcp-btn--sm">Register Now</Cta>
        </div>
      </nav>

      <header className="pcp-hero">
        <div className="pcp-hero__copy">
          {page.eyebrow && (
            <p className="pcp-eyebrow"><span className="pcp-rule" />{page.eyebrow}</p>
          )}
          <h1 className="pcp-h1">{camp.name.toUpperCase()}</h1>
          {page.tagline && <p className="pcp-tagline">{page.tagline}</p>}
          <dl className="pcp-facts">
            <div><dt>Date</dt><dd>{dateStr}</dd></div>
            <div>
              <dt>Location</dt>
              <dd>{page.location?.name || camp.venue || camp.location}</dd>
            </div>
          </dl>
          <div className="pcp-hero__buttons">
            <Cta className="pcp-btn pcp-btn--green">Register Now →</Cta>
            {has(page.schedule?.groups) && (
              <a href="#schedule" className="pcp-btn pcp-btn--ghost">View Schedule</a>
            )}
          </div>
        </div>
        {page.hero_image_url && (
          <img className="pcp-hero__art" src={page.hero_image_url} alt="" />
        )}
      </header>

      {has(page.stats) && (
        <section className="pcp-stats" data-testid="camp_page_stats">
          {page.stats.map((s, i) => (
            <div key={i} className="pcp-stat">
              <div className="pcp-stat__value">{s.value}</div>
              <div className="pcp-stat__caption">{s.caption}</div>
            </div>
          ))}
        </section>
      )}

      {has(page.schedule?.groups) && (
        <section id="schedule" className="pcp-section pcp-section--light"
                 data-testid="camp_page_schedule">
          <p className="pcp-eyebrow pcp-eyebrow--green">Event Schedule</p>
          {page.schedule.intro && <p className="pcp-lede">{page.schedule.intro}</p>}
          <div className="pcp-cards">
            {page.schedule.groups.map((g, i) => (
              <div key={i} className="pcp-card">
                <div className="pcp-card__label">{g.label}</div>
                <div className="pcp-card__title">{g.title}</div>
                {g.checkin && (
                  <div className="pcp-kv"><span>Check-in</span><b>{g.checkin}</b></div>
                )}
                {g.field_time && (
                  <div className="pcp-kv"><span>On field</span><b>{g.field_time}</b></div>
                )}
              </div>
            ))}
          </div>
          {page.schedule.footnote && (
            <p className="pcp-footnote">{page.schedule.footnote}</p>
          )}
        </section>
      )}

      {(has(page.about?.paragraphs) || has(page.about?.benefits)
        || has(page.testing?.measurables)) && (
        <section className="pcp-section pcp-section--tint pcp-about"
                 data-testid="camp_page_about">
          <div>
            {page.about?.heading && <h2 className="pcp-h2">{page.about.heading}</h2>}
            {(page.about?.paragraphs || []).map((p, i) => (
              <p key={i} className="pcp-body">{p}</p>
            ))}
            {has(page.testing?.measurables) && (
              <div id="testing" className="pcp-testing" data-testid="camp_page_testing">
                <div className="pcp-testing__head">Verified Testing</div>
                <div className="pcp-pills">
                  {page.testing.measurables.map((m, i) => (
                    <span key={i} className="pcp-pill">{m}</span>
                  ))}
                </div>
                <p className="pcp-footnote">
                  Only these measurables will be verified at this event — taken by
                  ProZpects staff and published to your recruiting profile.
                </p>
              </div>
            )}
          </div>
          {has(page.about?.benefits) && (
            <ul className="pcp-benefits">
              {page.about.benefits.map((b, i) => (
                <li key={i}><span className="pcp-check">✓</span>{b}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="pcp-section pcp-section--dark pcp-pricing">
        <div>
          <h2 className="pcp-h2 pcp-h2--light">Registration &amp; Pricing</h2>
          <p className="pcp-body pcp-body--muted">
            Select your option and see pricing on the registration page. Spots are
            limited, and athletes may only work out at the position they registered for.
          </p>
        </div>
        <Cta className="pcp-btn pcp-btn--green">Select Option &amp; Register →</Cta>
      </section>

      {(has(page.bring) || has(page.important) || hasLocation || hasContact) && (
        <section className="pcp-section pcp-section--light pcp-ready">
          <div>
            {has(page.bring) && (
              <>
                <p className="pcp-eyebrow">Be Ready</p>
                <h2 className="pcp-h2">What to Bring</h2>
                <ol className="pcp-numbered">
                  {page.bring.map((b, i) => (
                    <li key={i}><span>{String(i + 1).padStart(2, '0')}</span>{b}</li>
                  ))}
                </ol>
              </>
            )}
            {has(page.important) && (
              <>
                <p className="pcp-eyebrow pcp-eyebrow--green">Important Information</p>
                <ol className="pcp-numbered">
                  {page.important.map((b, i) => (
                    <li key={i}><span>{String(i + 1).padStart(2, '0')}</span>{b}</li>
                  ))}
                </ol>
              </>
            )}
          </div>
          <div>
            {hasLocation && (
              <div data-testid="camp_page_location">
                <p className="pcp-eyebrow">Location</p>
                {page.location.name && <h2 className="pcp-h2">{page.location.name}</h2>}
                <div className="pcp-card">
                  {(page.location.address_lines || []).map((l, i) => (
                    <div key={i} className="pcp-mono">{l}</div>
                  ))}
                  {page.location.maps_url && isSafeHttpUrl(page.location.maps_url) && (
                    <a className="pcp-link" href={page.location.maps_url}
                       target="_blank" rel="noreferrer">Get Directions →</a>
                  )}
                </div>
              </div>
            )}
            {hasContact && (
              <div className="pcp-card" data-testid="camp_page_contact">
                <div className="pcp-card__label">Questions</div>
                {page.contact.name && <div>{page.contact.name}</div>}
                {page.contact.email && (
                  <a className="pcp-link" href={`mailto:${page.contact.email}`}>
                    {page.contact.email}
                  </a>
                )}
                {page.contact.phone && <div className="pcp-mono">{page.contact.phone}</div>}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="pcp-cta">
        <div>
          <h2 className="pcp-h2 pcp-h2--light">
            {page.cta?.heading || 'Spots are limited. Lock in your position.'}
          </h2>
          {page.cta?.sub && <p className="pcp-cta__sub">{page.cta.sub}</p>}
        </div>
        <Cta className="pcp-btn pcp-btn--white">Register Now →</Cta>
      </section>

      <footer className="pcp-footer">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pcp-footer__logo" />
        <span className="pcp-footer__tag">Real Data. Real Results.</span>
        <span>© {new Date().getFullYear()} ProZpects Recruiting Solutions LLC</span>
      </footer>
    </div>
  );
}
